import { AfterViewInit, Component, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { commonImports } from '../shared/table-interface';
import { MatRadioModule } from '@angular/material/radio';
import { MatCalendar, MatCalendarCellClassFunction, MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { HttpClient, HttpContext } from '@angular/common/http';
import { EMPTY, Observable, catchError, combineLatest, debounceTime, distinctUntilChanged, expand, finalize, forkJoin, map, of, reduce, startWith, switchMap, tap } from 'rxjs'
import { JsonPipe } from '@angular/common';
import { ScheduleService } from './schedule.service';
import { appointmentTypesToken, backendEndPointToken } from '../app.config';
import { ActivatedRoute } from '@angular/router';
import { ErrorService } from '../shared/error.service';
import { FhirResourceService } from '../shared/fhir-resource.service';
import { DisableCache, DisableCountParam, DisableSortParam, LoadingUIEnabled } from '../loading.interceptor';
import type { Bundle, Practitioner, Schedule, Slot } from 'fhir/r4';
import { AuthService } from '../shared/auth/auth.service';
import { UtilityService } from '../shared/utility.service';
import { MatDialog } from '@angular/material/dialog';
import { DaySelectionDialogComponent } from '../shared/day-selection-dialog/day-selection-dialog.component';
import { isHospitalAdmin, normalizeRole } from '../shared/system-roles';
type TruthSlot = { dateTimeStart: string; dateTimeEnd: string };

type SlotKey = string;

type EditSlotSnapshot = {
  id: string;
  key: SlotKey;
  resource: Slot;
  status: string;
};

type EditScheduleSnapshot = {
  scheduleId: string;
  schedule: Schedule;
  planningHorizonStart: string | null;
  planningHorizonEnd: string | null;
  slots: EditSlotSnapshot[];
  slotsByKey: Map<SlotKey, EditSlotSnapshot>;
  bookedSlotsByKey: Map<SlotKey, EditSlotSnapshot>;
};

@Component({
  selector: 'app-create-schedule',
  imports: [...commonImports, ReactiveFormsModule,
    MatTimepickerModule, JsonPipe,
    MatRadioModule, MatDatepickerModule, MatMenuModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './create-schedule.component.html',
  styleUrl: './create-schedule.component.scss',
  providers: [provideNativeDateAdapter()]
})
export class CreateScheduleComponent implements AfterViewInit {

  fb = inject(FormBuilder);
  scheduleService = inject(ScheduleService);
  http = inject(HttpClient);
  backendUrl = inject(backendEndPointToken);
  appointmentTypes = inject(appointmentTypesToken);
  route = inject(ActivatedRoute);
  errorService = inject(ErrorService);
  fhirResourceService = inject(FhirResourceService);
  auth = inject(AuthService);
  utilityService = inject(UtilityService);
  dialog = inject(MatDialog);
  scheduleForm?: FormGroup;

  @ViewChild('previewSD')
  private previewCalendar?: MatCalendar<Date>;

  private planningHorizonLocalMs: { startMs: number; endMs: number } | null = null;
  // NOTE: `slotsPerWeekday` is separate from scheduleForm right now.
  // It stores *time ranges* for each weekday (start/end times).
  subGroupsVisibility = {
    'date': true,
    'dateRecurrence': false,
    'time': false,
    'timeRecurrence': false
  }

  setValue(event: any, control: AbstractControl) {
    // Weekday chips write their selected values into `dateRecurrence.value`.
    // This lets us drive the weekday slot arrays from a single source of truth.
    const selected = Array.isArray(event?.value) ? event.value : (event?.value ? [event.value] : []);
    control.setValue(selected);
    control.markAsDirty();
    control.updateValueAndValidity();

    this.syncWeekdaySlotsFromRecurrence();
  }
  constructor() {

    this.scheduleForm = this.fb.group({
      title: [''],
      serviceCategory: [''],
      date: this.fb.group({
        start: [''],
        end: this.fb.group({
          type: ['specific_date'],
          value: ['']
        })
      }),
      dateRecurrence: this.fb.group({
        type: ['mon_fri'],
        value: [[]] // selected weekdays when type === 'weekdays'
      }),
      // Replaces the old single "time" group: each weekday has its own FormArray of {start,end} slot groups.
      timePerWeekday: this.slotsPerWeekday,

    });
    // Assuming you want to manage participants as an array

  }

  // Which weekday sections are currently active based on `dateRecurrence`.
  // This drives both UI display and which weekdays participate in `slotsTruthSource`.
  get selectedWeekdays(): string[] {
    const type = this.scheduleForm?.get(['dateRecurrence', 'type'])?.value;
    const raw = this.scheduleForm?.get(['dateRecurrence', 'value'])?.value;
    const all = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    if (type === 'all_days') return all;
    if (type === 'mon_fri') return all.slice(0, 5);

    // type === 'weekdays' (user selects chips)
    const selected: string[] = Array.isArray(raw) ? raw : [];
    const normalized = selected
      .map((w) => this.normalizeWeekdayKey(w))
      .filter((w): w is string => !!w);

    return [...new Set(normalized)].filter((w) => all.includes(w));
  }

  getSlotsArray(weekday: string): FormArray {
    const key = this.normalizeWeekdayKey(weekday) ?? weekday;
    const control = this.slotsPerWeekday.get(key);
    return (control instanceof FormArray) ? control : this.fb.array([]);
  }

  private getSlotsArrayControl(weekday: string): FormArray | null {
    const key = this.normalizeWeekdayKey(weekday) ?? weekday;
    const control = this.slotsPerWeekday.get(key);
    return (control instanceof FormArray) ? control : null;
  }

  private normalizeWeekdayKey(input: any): string | null {
    const weekday = (input ?? '').toString().trim();
    if (!weekday) return null;
    const key = weekday.toLowerCase();
    const map: Record<string, string> = {
      monday: 'Monday', mon: 'Monday',
      tuesday: 'Tuesday', tue: 'Tuesday', tues: 'Tuesday',
      wednesday: 'Wednesday', wed: 'Wednesday',
      thursday: 'Thursday', thu: 'Thursday', thur: 'Thursday', thurs: 'Thursday',
      friday: 'Friday', fri: 'Friday',
      saturday: 'Saturday', sat: 'Saturday',
      sunday: 'Sunday', sun: 'Sunday',
    };
    return map[key] ?? null;
  }

  private syncWeekdaySlotsFromRecurrence(): void {
    // Enable only the selected weekday arrays and disable the rest.
    // We *do not clear* unselected days: this preserves previously-entered values
    // if the user later reselects the day, while preventing stale data from
    // being included in `scheduleForm.value` (disabled controls are excluded).
    const allWeekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const selected = new Set(this.selectedWeekdays);

    for (const weekday of allWeekdays) {
      const control = this.slotsPerWeekday.get(weekday);
      if (!control || !(control instanceof FormArray)) continue;

      if (selected.has(weekday)) {
        if (control.disabled) control.enable({ emitEvent: false });

        // Ensure each selected weekday has at least one slot group.
        // This makes the UX smoother: once a day is selected, you can immediately pick times.
        if (control.length === 0) {
          control.push(this.createSlotGroup());
        }

        control.updateValueAndValidity({ emitEvent: false });
      } else {
        if (control.enabled) control.disable({ emitEvent: false });
      }
    }
  }

  slotsPerWeekday: FormGroup = this.fb.group({
    Monday: this.fb.array([]),
    Tuesday: this.fb.array([]),
    Wednesday: this.fb.array([]),
    Thursday: this.fb.array([]),
    Friday: this.fb.array([]),
    Saturday: this.fb.array([]),
    Sunday: this.fb.array([]),
  });

  mondaySlots = this.slotsPerWeekday.get('Monday') as FormArray;
  tuesdaySlots = this.slotsPerWeekday.get('Tuesday') as FormArray;
  wednesdaySlots = this.slotsPerWeekday.get('Wednesday') as FormArray;
  thursdaySlots = this.slotsPerWeekday.get('Thursday') as FormArray;
  fridaySlots = this.slotsPerWeekday.get('Friday') as FormArray;
  saturdaySlots = this.slotsPerWeekday.get('Saturday') as FormArray;
  sundaySlots = this.slotsPerWeekday.get('Sunday') as FormArray;

  readonly slotSplitDurations = [15, 30, 45, 60, 90];
  private pendingSlotAction: { weekday: string; index: number } | null = null;

  slotsTruthSource: TruthSlot[] = [];
  savedSlots: Slot[] = [];
  loadingSavedSlots = false;
  practitionerOptions: Array<{ reference: string; display: string }> = [];
  selectedPractitionerReference: string | null = null;
  loadingPractitioners = false;

// Saved slot day keys for calendar highlighting (separate from form preview)
private savedSlotDayKeys = new Set<string>();

  // Preview calendar: only enable/select days that actually have generated slots.
  private previewSlotDayKeys = new Set<string>();

  readonly todayMinDate: Date = this.startOfLocalDay(new Date());

  get endMinDate(): Date {
    const startValue = this.scheduleForm?.get(['date', 'start'])?.value;
    const start = startValue instanceof Date ? startValue : (startValue ? new Date(startValue) : null);
    if (start && Number.isFinite(start.getTime())) return this.startOfLocalDay(start);
    return this.todayMinDate;
  }

  planningHorizonConflictSchedules: Schedule[] = [];
  planningHorizonConflictCheckFailed = false;
  planningHorizonConflictLoading = false;

  mode: 'create' | 'edit' = 'create';
  editSnapshot: EditScheduleSnapshot | null = null;

  addSlot(weekday: string) {
    // Adds a new slot group `{start,end}` into the weekday FormArray.
    // The group has:
    // - required validators on start/end
    // - a custom validator to enforce start < end
    const weekdayKey = this.normalizeWeekdayKey(weekday) ?? weekday;
    const control = this.slotsPerWeekday.get(weekdayKey);
    if (!control || !(control instanceof FormArray)) return;

    control.push(this.createSlotGroup());
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  addSlotAtNextIndex(weekday?: string, index?: number) {
    // Inserts a new time slot row *after* the provided index for the weekday.
    // Used by template:
    //   addSlotAtNextIndex(weekday, $index)
    if (!weekday) return;

    const weekdayKey = this.normalizeWeekdayKey(weekday) ?? weekday;
    const control = this.slotsPerWeekday.get(weekdayKey);
    if (!control || !(control instanceof FormArray)) return;

    const currentIndex = (typeof index === 'number' && Number.isFinite(index)) ? index : (control.length - 1);
    const insertIndex = Math.min(Math.max(currentIndex + 1, 0), control.length);

    const nextGroup = this.createSlotGroup();

    control.insert(insertIndex, nextGroup);
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  removeSlotAtIndex(weekday?: string, index?: number) {
    // Removes a time slot row from the `slotsPerWeekday` FormGroup.
    //
    // Intended usage from template:
    //   removeSlotAtIndex('Monday', i)
    //
    // Guard behavior:
    // - If weekday is missing/invalid -> no-op.
    // - If index is missing -> removes the last slot for that weekday.

    if (!weekday) return;

    // Normalize weekday input so callers can pass "monday", "MONDAY", etc.
    const weekdayKey = (() => {
      const key = weekday.trim().toLowerCase();
      const map: Record<string, string> = {
        monday: 'Monday', mon: 'Monday',
        tuesday: 'Tuesday', tue: 'Tuesday', tues: 'Tuesday',
        wednesday: 'Wednesday', wed: 'Wednesday',
        thursday: 'Thursday', thu: 'Thursday', thur: 'Thursday', thurs: 'Thursday',
        friday: 'Friday', fri: 'Friday',
        saturday: 'Saturday', sat: 'Saturday',
        sunday: 'Sunday', sun: 'Sunday',
      };
      return map[key] ?? weekday;
    })();

    const control = this.slotsPerWeekday.get(weekdayKey);
    if (!control || !(control instanceof FormArray)) return;

    const removeIndex = (index === undefined || index === null) ? (control.length - 1) : index;
    if (removeIndex < 0 || removeIndex >= control.length) return;

    control.removeAt(removeIndex);
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  setPendingSlotContext(weekday: string, index: number): void {
    this.pendingSlotAction = { weekday, index };
  }

  handleSplitSelection(minutes: number): void {
    if (!this.pendingSlotAction) return;
    this.splitSlotRange(this.pendingSlotAction.weekday, this.pendingSlotAction.index, minutes);
    this.pendingSlotAction = null;
  }

  openSlotApplyDialog(weekday: string, index: number): void {
    const normalized = this.normalizeWeekdayKey(weekday);
    if (!normalized) return;

    const dialogRef = this.dialog.open(DaySelectionDialogComponent, {
      data: {
        title: 'Apply slot to other days',
        description: 'Copy this exact time range to additional weekdays.',
        excludeDays: [normalized],
        confirmLabel: 'Apply'
      }
    });

    dialogRef.afterClosed().subscribe((days?: string[]) => {
      if (!Array.isArray(days) || days.length === 0) return;
      this.applySlotToWeekdays(normalized, index, days);
    });
  }

  openDayApplyDialog(weekday: string): void {
    const normalized = this.normalizeWeekdayKey(weekday);
    if (!normalized) return;

    const dialogRef = this.dialog.open(DaySelectionDialogComponent, {
      data: {
        title: `Apply ${normalized} slots to other days`,
        description: 'Overwrite selected weekdays with every slot defined for this day.',
        excludeDays: [normalized],
        preselectedDays: this.selectedWeekdays.filter((day) => day !== normalized),
        confirmLabel: 'Apply'
      }
    });

    dialogRef.afterClosed().subscribe((days?: string[]) => {
      if (!Array.isArray(days) || days.length === 0) return;
      this.applyDayToTargets(normalized, days);
    });
  }

  private splitSlotRange(weekday: string, index: number, intervalMinutes: number): void {
    if (!intervalMinutes || intervalMinutes < 5) {
      this.errorService.openandCloseError('Choose a larger interval before splitting.');
      return;
    }

    const array = this.getSlotsArrayControl(weekday);
    const group = array?.at(index) as FormGroup | undefined;
    if (!array || !group) return;

    const startMinutes = this.timeValueToMinutes(group.get('start')?.value);
    const endMinutes = this.timeValueToMinutes(group.get('end')?.value);
    if (startMinutes === null || endMinutes === null) return;

    const totalMinutes = endMinutes - startMinutes;
    if (totalMinutes <= intervalMinutes || totalMinutes % intervalMinutes !== 0) {
      group.setErrors({ ...(group.errors ?? {}), splitInvalid: true });
      this.errorService.openandCloseError('Slot cannot be evenly divided by the chosen interval.');
      return;
    }

    const segments: Array<{ start: number; end: number }> = [];
    let cursor = startMinutes;
    while (cursor < endMinutes) {
      const next = cursor + intervalMinutes;
      segments.push({ start: cursor, end: next });
      cursor = next;
    }

    array.removeAt(index);
    segments.forEach((segment, offset) => {
      const newGroup = this.createSlotGroupWithValues(
        this.minutesToHHmm(segment.start),
        this.minutesToHHmm(segment.end)
      );
      array.insert(index + offset, newGroup);
    });

    array.markAsDirty();
    array.updateValueAndValidity();
    this.recalculateTruthSource();
  }

  private applySlotToWeekdays(sourceDay: string, sourceIndex: number, targetDays: string[]): void {
    const normalizedTargets = (targetDays || [])
      .map((day) => this.normalizeWeekdayKey(day))
      .filter((day): day is string => !!day && day !== sourceDay);

    if (!normalizedTargets.length) return;

    const sourceArray = this.getSlotsArrayControl(sourceDay);
    const sourceGroup = sourceArray?.at(sourceIndex) as FormGroup | undefined;
    if (!sourceArray || !sourceGroup) return;

    const startHHmm = this.timeValueToHHmm(sourceGroup.get('start')?.value);
    const endHHmm = this.timeValueToHHmm(sourceGroup.get('end')?.value);
    if (!startHHmm || !endHHmm) return;

    this.ensureWeekdaysActive(normalizedTargets);

    normalizedTargets.forEach((day) => {
      const targetArray = this.getSlotsArrayControl(day);
      if (!targetArray) return;
      const duplicate = targetArray.controls.some((ctrl) => {
        const ctrlStart = this.timeValueToHHmm(ctrl.get('start')?.value);
        const ctrlEnd = this.timeValueToHHmm(ctrl.get('end')?.value);
        return ctrlStart === startHHmm && ctrlEnd === endHHmm;
      });
      if (duplicate) return;

      targetArray.push(this.createSlotGroupWithValues(startHHmm, endHHmm));
      targetArray.markAsDirty();
      targetArray.updateValueAndValidity({ emitEvent: false });
    });

    this.recalculateTruthSource();
  }

  private applyDayToTargets(sourceDay: string, targetDays: string[]): void {
    const normalizedTargets = (targetDays || [])
      .map((day) => this.normalizeWeekdayKey(day))
      .filter((day): day is string => !!day && day !== sourceDay);

    if (!normalizedTargets.length) return;

    const sourceArray = this.getSlotsArrayControl(sourceDay);
    if (!sourceArray || sourceArray.length === 0) {
      this.errorService.openandCloseError(`Add at least one slot to ${sourceDay} before applying it to other days.`);
      return;
    }

    const ranges = sourceArray.controls
      .map((ctrl) => {
        const startHHmm = this.timeValueToHHmm(ctrl.get('start')?.value);
        const endHHmm = this.timeValueToHHmm(ctrl.get('end')?.value);
        if (!startHHmm || !endHHmm) return null;
        return { start: startHHmm, end: endHHmm };
      })
      .filter((range): range is { start: string; end: string } => !!range);

    if (!ranges.length) return;

    this.ensureWeekdaysActive(normalizedTargets);

    normalizedTargets.forEach((day) => {
      const targetArray = this.getSlotsArrayControl(day);
      if (!targetArray) return;
      while (targetArray.length > 0) {
        targetArray.removeAt(0);
      }
      ranges.forEach((range) => targetArray.push(this.createSlotGroupWithValues(range.start, range.end)));
      targetArray.markAsDirty();
      targetArray.updateValueAndValidity({ emitEvent: false });
    });

    this.recalculateTruthSource();
  }

  private ensureWeekdaysActive(days: string[]): void {
    if (!this.scheduleForm || !days?.length) return;
    const daySet = new Set(days.map((d) => this.normalizeWeekdayKey(d)).filter((d): d is string => !!d));
    if (daySet.size === 0) return;

    const typeCtrl = this.scheduleForm.get(['dateRecurrence', 'type']);
    const valueCtrl = this.scheduleForm.get(['dateRecurrence', 'value']);
    if (!typeCtrl || !valueCtrl) return;

    const currentSelection = new Set(this.selectedWeekdays);
    let requiresCustomType = false;
    daySet.forEach((day) => {
      if (!currentSelection.has(day)) {
        requiresCustomType = true;
        currentSelection.add(day);
      }
    });

    if (!requiresCustomType) {
      return;
    }

    typeCtrl.setValue('weekdays');
    valueCtrl.setValue(Array.from(currentSelection));
    this.syncWeekdaySlotsFromRecurrence();
  }

  private createSlotGroupWithValues(startHHmm: string, endHHmm: string): FormGroup {
    const group = this.createSlotGroup();
    const startDate = this.hhmmToDate(startHHmm);
    const endDate = this.hhmmToDate(endHHmm);
    group.patchValue(
      {
        start: startDate ?? startHHmm,
        end: endDate ?? endHHmm
      },
      { emitEvent: false }
    );
    return group;
  }


  isAdmin = false;

  isAdminUser(): boolean {
    const user: any = this.auth.user.getValue();
    const role = user?.role;
    // alert(`User role: ${role}, isHospitalAdmin: ${isHospitalAdmin(role)}, normalizedRole: ${normalizeRole(role)}`);
    return isHospitalAdmin(role) || normalizeRole(role) === 'superadmin';
  }

  onPractitionerSelected(reference: string): void {
    this.selectedPractitionerReference = reference || null;
    this.loadSavedSlotsForPreview();
    this.checkPlanningHorizonConflicts({ showBlockingError: false, hardFailOnError: false }).subscribe();
  }

  get selectedPractitionerDisplay(): string {
    const ref = this.selectedPractitionerReference ?? this.getCurrentActorReference();
    if (!ref) return 'No practitioner selected';
    return this.practitionerOptions.find((option) => option.reference === ref)?.display ?? ref;
  }

  get isSelectedPractitionerLoggedInUser(): boolean {
    const user: any = this.auth.user.getValue();
    const myId = (user?.fhirResourceId ?? user?.userId ?? '').toString().trim();
    if (!myId) return false;
    const myRef = `Practitioner/${myId}`;
    const currentRef = this.selectedPractitionerReference ?? this.getCurrentActorReference();
    return currentRef === myRef || (currentRef ? currentRef.endsWith(`/${myId}`) : false);
  }

  private loadPractitionerOptionsForAdmin(): void {
    if (!this.isAdminUser() || this.mode === 'edit') return;

    const user: any = this.auth.user.getValue();
    const myId = (user?.fhirResourceId ?? user?.userId ?? '').toString().trim();
    const myRef = myId ? `Practitioner/${myId}` : null;

    this.loadingPractitioners = true;
    const ctx = new HttpContext().set(LoadingUIEnabled, false);
    this.fetchAllBundleResources<Practitioner>(`${this.backendUrl}/Practitioner?_count=200`, ctx).pipe(
      map((resources) => resources.filter((resource): resource is Practitioner => resource?.resourceType === 'Practitioner')),
      catchError(() => of([] as Practitioner[])),
      finalize(() => this.loadingPractitioners = false)
    ).subscribe((practitioners) => {
      let options = practitioners
        .filter((practitioner) => !!practitioner.id)
        .map((practitioner) => ({
          reference: `Practitioner/${practitioner.id}`,
          display: this.getPractitionerDisplay(practitioner)
        }));

      // Ensure logged-in admin's own practitioner record is present in the list
      if (myRef && !options.some(opt => opt.reference === myRef)) {
        const selfName = user.name || user.email || 'Practitioner';
        options.unshift({
          reference: myRef,
          display: selfName
        });
      }

      this.practitionerOptions = options.sort((a, b) => a.display.localeCompare(b.display));

      // If no practitioner is selected yet, default to admin's own reference or first option
      if (!this.selectedPractitionerReference) {
        if (myRef && this.practitionerOptions.some(opt => opt.reference === myRef)) {
          this.onPractitionerSelected(myRef);
        } else if (this.practitionerOptions.length > 0) {
          this.onPractitionerSelected(this.practitionerOptions[0].reference);
        }
      }
    });
  }

  private getPractitionerDisplay(practitioner: Practitioner): string {
    const name = practitioner.name?.[0];
    const prefix = Array.isArray(name?.prefix) ? name?.prefix?.join(' ') : '';
    const given = Array.isArray(name?.given) ? name?.given?.join(' ') : '';
    const family = name?.family ?? '';
    const text = name?.text || [prefix, given, family].filter(Boolean).join(' ');
    return text?.trim() || `Practitioner/${practitioner.id}`;
  }

  loadSavedSlotsForPreview(): void {
    // ✅ Use fhirResourceId if available, fall back to id
    const actorRef = this.getCurrentActorReference();
    if (!actorRef) {
      this.savedSlots = [];
      this.savedSlotDayKeys.clear();
      this.refreshPreviewCalendar();
      return;
    }

    this.loadingSavedSlots = true;
    const ctx = new HttpContext().set(LoadingUIEnabled, false);

    this.fetchAllBundleResources<Schedule>(
        `${this.backendUrl}/Schedule?actor=${actorRef}&active=true&_count=50`,
        ctx
    ).subscribe({
        next: (resources) => {
            const allEntries = resources;

            // ✅ Filter out OperationOutcome entries (warnings from server)
            // and filter to only schedules belonging to THIS practitioner
            const schedules = allEntries
                .filter((r): r is Schedule =>
                    r?.resourceType === 'Schedule' &&
                    // ✅ Client-side filter: only this practitioner's schedules
                    ((r as any).actor ?? []).some((a: any) => {
                        const ref = String(a?.reference ?? '').trim();
                        // Handle both "Practitioner/id" and full URL "https://.../Practitioner/id"
                        return ref === actorRef || ref.endsWith(`/${actorRef}`);
                    })
                );

            if (schedules.length === 0) {
                this.savedSlots = [];
                this.savedSlotDayKeys.clear();
                this.loadingSavedSlots = false;
                this.refreshPreviewCalendar();
                return;
            }

            const scheduleIds = schedules
                .map(s => s.id)
                .filter((id): id is string => !!id);

            // ✅ Fetch slots for each schedule in parallel
            const requests = scheduleIds.map(id =>
                this.fetchAllBundleResources<Slot>(
                    `${this.backendUrl}/Slot?schedule=Schedule/${encodeURIComponent(id)}&status=free&_count=200`,
                    ctx
                ).pipe(
                    map((slots) => slots.filter((r): r is Slot => r?.resourceType === 'Slot')),
                    catchError(() => of([] as Slot[]))
                )
            );

            forkJoin(requests).subscribe({
                next: (slotLists) => {
                    this.setSavedSlotsForPreview(slotLists.flat());
                    this.loadingSavedSlots = false;
                },
                error: () => { this.loadingSavedSlots = false; }
            });
        },
        error: () => { this.loadingSavedSlots = false; }
    });
}

  ngOnInit() {
    this.isAdmin = this.isAdminUser();
    this.auth.user.subscribe(() => {
      this.isAdmin = this.isAdminUser();
    });

    // If a scheduleId is provided (query param or route param), load for editing.
    const scheduleId =
      this.route.snapshot.paramMap.get('scheduleId') ??
      this.route.snapshot.paramMap.get('id') ??
      this.route.snapshot.queryParamMap.get('scheduleId') ??
      this.route.snapshot.queryParamMap.get('id');

    if (scheduleId) {
      this.mode = 'edit';
      this.loadScheduleForEdit(scheduleId);
    } else {
      this.loadPractitionerOptionsForAdmin();
      this.loadSavedSlotsForPreview();
    }

    // Attach overlap validators once so each weekday array rejects overlapping time ranges.
    this.attachWeekdayArrayValidators();

    // When date recurrence changes, show/sync weekday slot sections.
    this.syncWeekdaySlotsFromRecurrence();
    this.scheduleForm?.get(['dateRecurrence', 'type'])?.valueChanges
      .pipe(startWith(this.scheduleForm?.get(['dateRecurrence', 'type'])?.value))
      .subscribe(() => {
        this.syncWeekdaySlotsFromRecurrence();
        this.recalculateTruthSource();
      });
    this.scheduleForm?.get(['dateRecurrence', 'value'])?.valueChanges
      .pipe(startWith(this.scheduleForm?.get(['dateRecurrence', 'value'])?.value))
      .subscribe(() => {
        this.syncWeekdaySlotsFromRecurrence();
        this.recalculateTruthSource();
      });

    // Rebuild truth source whenever either:
    // - a slot time changes (slotsPerWeekday.valueChanges)
    // - the date interval changes (scheduleForm.date.* valueChanges)
    // but only when everything is valid.
    this.setupTruthSourceRecalculation();

    // Check planning-horizon conflicts on start/end changes (debounced).
    this.setupPlanningHorizonConflictRecalculation();

    // Keep the preview calendar highlights in sync with external state changes.
    this.setupPreviewCalendarRefresh();

  }

  ngAfterViewInit(): void {
    // Ensure the first paint uses the latest cached ranges/availability.
    this.refreshPreviewCalendar();
  }

  get hasPlanningHorizonConflict(): boolean {
    return this.planningHorizonConflictSchedules.length > 0;
  }

  /**
   * CREATE / EDIT SAVE
   *
   * Business rule for editing:
   * - If any change would remove an already-booked slot, BLOCK the save.
   *   (We treat slots as booked when Slot.status is not 'free'.)
   */
  save(): void {
    if (!this.scheduleForm) return;

    // Require date interval.
    const dateIntervalStart = this.scheduleForm.get(['date', 'start'])?.value;
    const dateIntervalEnd = this.scheduleForm.get(['date', 'end', 'value'])?.value;
    if (!dateIntervalStart || !dateIntervalEnd) {
      this.errorService.openandCloseError('Please set a start date and end date.');
      return;
    }

    // Always re-check schedule conflicts right before submission.
    this.checkPlanningHorizonConflicts({ showBlockingError: true, hardFailOnError: true }).subscribe((okToProceed) => {
      if (!okToProceed) return;
      this.performSave();
    });
  }

  private performSave(): void {
    if (!this.scheduleForm) return;

    const dateIntervalStart = this.scheduleForm.get(['date', 'start'])?.value;
    const dateIntervalEnd = this.scheduleForm.get(['date', 'end', 'value'])?.value;
    if (!dateIntervalStart || !dateIntervalEnd) {
      this.errorService.openandCloseError('Please set a start date and end date.');
      return;
    }

    // Ensure generated truth source is current and valid.
    this.recalculateTruthSource();
    if (this.slotsTruthSource.length === 0) {
      this.errorService.openandCloseError('No slots generated. Please add valid time slots.');
      return;
    }

    if (this.mode === 'edit') {
      if (!this.editSnapshot) {
        this.errorService.openandCloseError('Edit context not loaded. Please refresh and try again.');
        return;
      }

      const diff = this.diffSlots(this.editSnapshot, this.slotsTruthSource);
      const horizonChanged = this.hasPlanningHorizonChanged(this.editSnapshot);

      if (!horizonChanged && diff.toCreate.length === 0 && diff.toRetire.length === 0) {
        this.errorService.openandCloseError('No changes to save.');
        return;
      }
      const bookedToRetire = diff.toRetire.filter((s) => this.isBookedSlotStatus(s.status));
      if (bookedToRetire.length > 0) {
        const preview = bookedToRetire
          .slice(0, 4)
          .map((s) => this.formatSlotForUser(s.resource))
          .join('; ');
        const suffix = bookedToRetire.length > 4 ? ` (and ${bookedToRetire.length - 4} more)` : '';
        this.errorService.openandCloseError(
          `Cannot retire booked slots. Remove/modify booked slots is not allowed. Affected: ${preview}${suffix}`
        );
        return;
      }

      const bundle = this.buildEditTransactionBundle(this.editSnapshot, diff, horizonChanged);
      this.fhirResourceService.postBundle(bundle).subscribe({
        next: () => {
          // Best-effort: reload edit context so the "before" snapshot matches server.
          this.loadScheduleForEdit(this.editSnapshot!.scheduleId);
        },
        error: () => this.errorService.openandCloseError('Failed to save schedule changes.')
      });
      return;
    }

    // CREATE mode
    const actorRef = this.getCurrentActorReference();
    if (!actorRef) {
      this.errorService.openandCloseError(this.isAdminUser()
        ? 'Select the practitioner this schedule is for.'
        : 'Cannot create schedule: no practitioner is logged in.');
      return;
    }

    const bundle = this.buildCreateTransactionBundle({
      planningHorizonStart: dateIntervalStart,
      planningHorizonEnd: dateIntervalEnd,
      actorReference: actorRef,
      slots: this.slotsTruthSource,
    });

    this.fhirResourceService.postBundle(bundle).subscribe({
      next: () => {
        // Keep the form as-is, but clear preview so user sees success and doesn't resubmit.
        this.slotsTruthSource = [];
        this.onTruthSourceCleared();
        this.loadSavedSlotsForPreview();
      },
      error: () => this.errorService.openandCloseError('Failed to create schedule.')
    });
  }

  private setupPlanningHorizonConflictRecalculation(): void {
    if (!this.scheduleForm) return;

    const startCtrl = this.scheduleForm.get(['date', 'start']);
    const endCtrl = this.scheduleForm.get(['date', 'end', 'value']);
    if (!startCtrl || !endCtrl) return;

    combineLatest([
      startCtrl.valueChanges.pipe(startWith(startCtrl.value)),
      endCtrl.valueChanges.pipe(startWith(endCtrl.value)),
    ])
      .pipe(
        debounceTime(250),
        map(([startValue, endValue]) => ({
          startIso: this.normalizeInstant(startValue),
          endIso: this.normalizeInstant(endValue),
        })),
        distinctUntilChanged((a, b) => a.startIso === b.startIso && a.endIso === b.endIso),
        switchMap(() => this.checkPlanningHorizonConflicts({ showBlockingError: false, hardFailOnError: false }))
      )
      .subscribe();
  }

  private checkPlanningHorizonConflicts(options: { showBlockingError: boolean; hardFailOnError: boolean }) {
    // Returns Observable<boolean>: true means OK to proceed, false means block (conflict or hard failure).
    if (!this.scheduleForm) return of(true);

    const startValue = this.scheduleForm.get(['date', 'start'])?.value;
    const endValue = this.scheduleForm.get(['date', 'end', 'value'])?.value;

    const startIso = this.normalizeInstant(startValue);
    const endIso = this.normalizeInstant(endValue);

    // If incomplete/invalid horizon, clear state and do not block.
    if (!startIso || !endIso) {
      this.clearPlanningHorizonConflictState();
      return of(true);
    }

    const startMs = new Date(startIso).getTime();
    const endMs = new Date(endIso).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
      this.clearPlanningHorizonConflictState();
      return of(true);
    }

    const actorRef = this.getActorReferenceForConflictQuery();
    if (!actorRef) {
      // Can't check without actor; treat as "cannot verify" but don't block on background checks.
      this.planningHorizonConflictSchedules = [];
      this.planningHorizonConflictCheckFailed = true;
      this.setScheduleDateConflictError(false);
      return of(!options.hardFailOnError);
    }

    const ctx = new HttpContext()
      .set(LoadingUIEnabled, false)
      .set(DisableSortParam, true)
      .set(DisableCountParam, true)
      .set(DisableCache, true);

    this.planningHorizonConflictLoading = true;
    this.planningHorizonConflictCheckFailed = false;

    const commonArgs = {
      resourceType: 'Schedule',
      scope: 'orgWide' as const,
      // count: 200,
      // context: ctx,
      tier1FollowNextLinks: true,
      tier1MaxPages: 10,
      tier3FollowNextLinks: true,
      tier3MaxPages: 10,
      // We want paging to continue until we find at least 1 locally-matching schedule.
      expectedMinLength: 1,
      // If tier-1 fails due to unsupported params, allow tier-3 to run even if state has < expected.
      tier3TriggerWhenBelowExpectedMinOnTier1Failure: true,
      querySearchObject: [
        { key: 'actor.reference', value: [actorRef, `${this.backendUrl}/${actorRef}`] },
        { key: 'active', value: true },
      ],
    };

    const tier1DateAndActor$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Schedule>({
      ...commonArgs,
      urlSearchObject: {
        active: true,
        actor: actorRef,
        date: [`ge${startIso}`, `le${endIso}`],
      },
    });

    const tier2ActorOnly$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Schedule>({
      ...commonArgs,
      urlSearchObject: {
        active: true,
        actor: actorRef,
      },
    });

    const tier3ActiveOnly$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Schedule>({
      ...commonArgs,
      urlSearchObject: {
        active: true,
      },
    });

    return tier1DateAndActor$.pipe(
      catchError(() => of([] as Schedule[])),
      switchMap((tier1List) => {
        if (Array.isArray(tier1List) && tier1List.length) return of(tier1List);
        return tier2ActorOnly$.pipe(catchError(() => of([] as Schedule[])));
      }),
      switchMap((tier12List) => {
        if (Array.isArray(tier12List) && tier12List.length) return of(tier12List);
        return tier3ActiveOnly$;
      }),
      map((schedules: Schedule[]) => {
        console.log(`Planning horizon conflict check: found ${schedules.length} candidate schedules to check for conflicts.`);

        const conflicts = this.filterConflictingSchedules({
          schedules,
          actorRef,
          startIso,
          endIso,
          ignoreScheduleId: this.mode === 'edit' ? (this.editSnapshot?.scheduleId ?? null) : null,
        });

        this.planningHorizonConflictSchedules = conflicts;
        this.planningHorizonConflictCheckFailed = false;
        this.setScheduleDateConflictError(conflicts.length > 0);

        if (conflicts.length > 0 && options.showBlockingError) {
          this.errorService.openandCloseError(
            `Planning horizon conflicts with ${conflicts.length} existing schedule(s). Please choose another date range.`
          );
        }

        return conflicts.length === 0;
      }),
      catchError(() => {
        this.planningHorizonConflictSchedules = [];
        this.planningHorizonConflictCheckFailed = true;
        this.setScheduleDateConflictError(false);

        if (options.hardFailOnError && options.showBlockingError) {
          this.errorService.openandCloseError('Could not verify schedule conflicts. Please check your connection and try again.');
        }

        return of(!options.hardFailOnError);
      }),
      finalize(() => {
        this.planningHorizonConflictLoading = false;
      })
    );
  }

  private getActorReferenceForConflictQuery(): string | null {
    // Prefer the schedule's actor in edit mode (more accurate than current user in edge cases).
    if (this.mode === 'edit') {
      const ref = (this.editSnapshot?.schedule as any)?.actor?.[0]?.reference;
      if (ref) return String(ref);
    }
    return this.getCurrentActorReference();
  }

  private filterConflictingSchedules(config: {
    schedules: Schedule[];
    actorRef: string;
    startIso: string;
    endIso: string;
    ignoreScheduleId: string | null;
  }): Schedule[] {
    const startMs = new Date(config.startIso).getTime();
    const endMs = new Date(config.endIso).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return [];

    return config.schedules
      .filter((s) => {
        const id = (s?.id ?? '').toString();
        if (config.ignoreScheduleId && id === config.ignoreScheduleId) return false;
        if ((s as any)?.active === false) return false;

        // Ensure we're comparing schedules for the same actor.
        const actors = ((s as any)?.actor ?? []) as Array<{ reference?: string }>;
        const hasActor = actors.some((a) => String(a?.reference ?? '') === config.actorRef ||
          //server with backed attached
          String(a?.reference ?? '') === `${this.backendUrl}/${config.actorRef}`
        );
        if (!hasActor) return false;

        const h = (s as any)?.planningHorizon;
        const otherStartIso = this.normalizeInstant(h?.start);
        const otherEndIso = this.normalizeInstant(h?.end);
        if (!otherStartIso || !otherEndIso) return false;

        const otherStartMs = new Date(otherStartIso).getTime();
        const otherEndMs = new Date(otherEndIso).getTime();
        if (!Number.isFinite(otherStartMs) || !Number.isFinite(otherEndMs) || otherEndMs <= otherStartMs) return false;

        // Half-open overlap: [aStart, aEnd) overlaps [bStart, bEnd) if aStart < bEnd && bStart < aEnd
        return startMs < otherEndMs && otherStartMs < endMs;
      })
      .slice(0, 25);
  }

  private clearPlanningHorizonConflictState(): void {
    this.planningHorizonConflictSchedules = [];
    this.planningHorizonConflictCheckFailed = false;
    this.planningHorizonConflictLoading = false;
    this.setScheduleDateConflictError(false);
  }

  private setScheduleDateConflictError(hasConflict: boolean): void {
    const dateGroup = this.scheduleForm?.get('date');
    if (!dateGroup) return;

    const existing = dateGroup.errors ?? {};
    if (hasConflict) {
      if (!existing['planningHorizonConflict']) {
        dateGroup.setErrors({ ...existing, planningHorizonConflict: true });
      }
      return;
    }

    if (existing['planningHorizonConflict']) {
      const { planningHorizonConflict, ...rest } = existing as any;
      dateGroup.setErrors(Object.keys(rest).length ? rest : null);
    }
  }

  private hasPlanningHorizonChanged(snapshot: EditScheduleSnapshot): boolean {
    const startValue = this.scheduleForm?.get(['date', 'start'])?.value;
    const endValue = this.scheduleForm?.get(['date', 'end', 'value'])?.value;
    const startIso = this.normalizeInstant(startValue);
    const endIso = this.normalizeInstant(endValue);

    const beforeStartIso = snapshot.planningHorizonStart ? this.normalizeInstant(snapshot.planningHorizonStart) : null;
    const beforeEndIso = snapshot.planningHorizonEnd ? this.normalizeInstant(snapshot.planningHorizonEnd) : null;

    return startIso !== beforeStartIso || endIso !== beforeEndIso;
  }

  private getCurrentActorReference(): string | null {
    if (this.isAdminUser() && this.mode !== 'edit') {
      if (this.selectedPractitionerReference) {
        return this.selectedPractitionerReference;
      }
      // Fallback to logged-in admin's own practitioner reference
      const u: any = this.auth.user.getValue();
      const id = (u?.fhirResourceId ?? u?.userId ?? '').toString().trim();
      return id ? `Practitioner/${id}` : null;
    }

    const u: any = this.auth.user.getValue();
    const id = (u?.fhirResourceId ?? u?.userId ?? '').toString().trim();
    if (!id) return null;
    return `Practitioner/${id}`;
  }

  private normalizeInstant(input: any): string | null {
    if (!input) return null;
    if (input instanceof Date) {
      if (Number.isNaN(input.getTime())) return null;
      return input.toISOString();
    }
    const raw = String(input).trim();
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  }

  private slotKey(start: string, end: string): SlotKey {
    return `${this.normalizeInstant(start) ?? start}|${this.normalizeInstant(end) ?? end}`;
  }

  private isBookedSlotStatus(status: any): boolean {
    const s = String(status ?? '').toLowerCase().trim();
    // Business rule: any non-free slot is considered booked/linked and cannot be retired by schedule edit.
    // This matches the app convention noted elsewhere that slot status changes automatically on booking.
    if (!s) return false;
    return s !== 'free' && s !== 'entered-in-error';
  }

  private formatSlotForUser(slot: Slot): string {
    const start = slot?.start ? new Date(slot.start) : null;
    const end = slot?.end ? new Date(slot.end) : null;
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'Unknown slot';
    return `${start.toLocaleString()} → ${end.toLocaleString()}`;
  }

private buildCreateTransactionBundle(config: {
    planningHorizonStart: any;
    planningHorizonEnd: any;
    actorReference: string;
    slots: TruthSlot[];
  }): Bundle {
    const startIso = this.normalizeInstant(config.planningHorizonStart);
    const endIso = this.normalizeInstant(config.planningHorizonEnd);
    if (!startIso || !endIso) {
      throw new Error('Invalid planning horizon');
    }

    const makeUrnUuid = () => {
      try {
        const anyCrypto: any = (globalThis as any)?.crypto;
        const id = anyCrypto?.randomUUID?.();
        return `urn:uuid:${id || Math.random().toString(16).slice(2)}`;
      } catch {
        return `urn:uuid:${Math.random().toString(16).slice(2)}`;
      }
    };

    const scheduleFullUrl = makeUrnUuid();
    const orgId = this.auth.user.getValue()?.organizationId || '';

    // ✅ FIX 1: Normalize orgId to handle both "xxx" and "Organization/xxx" formats
    const normalizedOrgId = orgId.startsWith('Organization/') ? orgId : `Organization/${orgId}`;

    const serviceCategoryCode = this.scheduleForm?.get('serviceCategory')?.value;
    const serviceCategory = serviceCategoryCode ? [{
      coding: [{
        system: `${this.backendUrl}/CodeSystem/appointment-type`,
        code: serviceCategoryCode
      }]
    }] : undefined;

    // ✅ FIX 2: Change type from Schedule to any (Schedule type doesn't support extension)
    const schedule: any = {
      resourceType: 'Schedule',
      active: true,
      actor: [{ reference: config.actorReference }],
      planningHorizon: { start: startIso, end: endIso },
      ...(serviceCategory && { serviceCategory }),

      ...(orgId && {
        extension: [
          {
            url: "http://your-org/fhir/StructureDefinition/actor-organization",
            valueReference: { reference: normalizedOrgId }  // ✅ Use normalized org ID
          }
        ]
      })
    };

    const entries: any[] = [
      {
        fullUrl: scheduleFullUrl,
        resource: schedule,
        request: { method: 'POST', url: 'Schedule' }
      }
    ];

    for (let i = 0; i < config.slots.length; i++) {
      const s = config.slots[i];
      const slot: Slot = {
        resourceType: 'Slot',
        status: 'free',
        schedule: { reference: scheduleFullUrl },
        start: this.normalizeInstant(s.dateTimeStart) ?? s.dateTimeStart,
        end: this.normalizeInstant(s.dateTimeEnd) ?? s.dateTimeEnd,
        ...(serviceCategory && { serviceCategory: serviceCategory as any })
      };

      entries.push({
        fullUrl: makeUrnUuid(),
        resource: slot,
        request: { method: 'POST', url: 'Slot' }
      });
    }

    return { resourceType: 'Bundle', type: 'transaction', entry: entries } as any;
  }

  private buildEditTransactionBundle(
    snapshot: EditScheduleSnapshot,
    diff: { toCreate: TruthSlot[]; toRetire: EditSlotSnapshot[] },
    horizonChanged: boolean
  ): Bundle {
    const startValue = this.scheduleForm?.get(['date', 'start'])?.value;
    const endValue = this.scheduleForm?.get(['date', 'end', 'value'])?.value;
    const startIso = this.normalizeInstant(startValue);
    const endIso = this.normalizeInstant(endValue);
    if (!startIso || !endIso) {
      throw new Error('Invalid planning horizon');
    }

    const entries: any[] = [];

    // Update Schedule planning horizon only when changed.
    if (horizonChanged) {
      const updatedSchedule: any = {
        ...(snapshot.schedule as any),
        resourceType: 'Schedule',
        id: snapshot.scheduleId,
        planningHorizon: { start: startIso, end: endIso },
      };

      const serviceCategoryCode = this.scheduleForm?.get('serviceCategory')?.value;
      if (serviceCategoryCode) {
        updatedSchedule.serviceCategory = [{
          coding: [{
            system: `${this.backendUrl}/CodeSystem/appointment-type`,
            code: serviceCategoryCode
          }]
        }];
      } else {
        delete updatedSchedule.serviceCategory;
      }

      entries.push({
        resource: updatedSchedule,
        request: { method: 'PUT', url: `Schedule/${snapshot.scheduleId}` }
      });
    }

    // Retire removed slots (only non-booked are allowed by save() guard).
    for (const retired of diff.toRetire) {
      const updated: Slot = {
        ...(retired.resource as any),
        resourceType: 'Slot',
        id: retired.id,
        status: 'entered-in-error',
      };

      entries.push({
        resource: updated,
        request: { method: 'PUT', url: `Slot/${retired.id}` }
      });
    }

    // Create new slots.
    const serviceCategoryCode = this.scheduleForm?.get('serviceCategory')?.value;
    const serviceCategory = serviceCategoryCode ? [{
      coding: [{
        system: `${this.backendUrl}/CodeSystem/appointment-type`,
        code: serviceCategoryCode
      }]
    }] : undefined;

    for (const s of diff.toCreate) {
      const slot: Slot = {
        resourceType: 'Slot',
        status: 'free',
        schedule: { reference: `Schedule/${snapshot.scheduleId}` },
        start: this.normalizeInstant(s.dateTimeStart) ?? s.dateTimeStart,
        end: this.normalizeInstant(s.dateTimeEnd) ?? s.dateTimeEnd,
        ...(serviceCategory && { serviceCategory: serviceCategory as any })
      };

      entries.push({
        resource: slot,
        request: { method: 'POST', url: 'Slot' }
      });
    }

    return { resourceType: 'Bundle', type: 'transaction', entry: entries } as any;
  }

  private diffSlots(snapshot: EditScheduleSnapshot, after: TruthSlot[]): { toCreate: TruthSlot[]; toRetire: EditSlotSnapshot[] } {
    const afterKeys = new Set(after.map((s) => this.slotKey(s.dateTimeStart, s.dateTimeEnd)));
    const toCreate: TruthSlot[] = [];
    for (const s of after) {
      const key = this.slotKey(s.dateTimeStart, s.dateTimeEnd);
      if (!snapshot.slotsByKey.has(key)) toCreate.push(s);
    }

    const toRetire: EditSlotSnapshot[] = [];
    for (const existing of snapshot.slots) {
      if (!afterKeys.has(existing.key)) toRetire.push(existing);
    }

    return { toCreate, toRetire };
  }

  private loadScheduleForEdit(scheduleId: string): void {
    const ctx = new HttpContext().set(DisableSortParam, true).set(DisableCountParam, true);

    this.http.get<Schedule>(`${this.backendUrl}/Schedule/${scheduleId}`, { context: ctx }).subscribe({
      next: (schedule) => {
        // Load all slots for the schedule.
        this.fetchAllBundleResources<Slot>(
          `${this.backendUrl}/Slot?schedule=Schedule/${scheduleId}&_count=200`,
          ctx
        ).subscribe({
          next: (resources) => {
            const slots = resources.filter((resource): resource is Slot => resource?.resourceType === 'Slot');
            this.setSavedSlotsForPreview(slots);
            const snapshot = this.buildEditSnapshot(scheduleId, schedule, slots);
            this.editSnapshot = snapshot;
            this.applySnapshotToForm(snapshot);
          },
          error: () => this.errorService.openandCloseError('Failed to load schedule slots.')
        });
      },
      error: () => this.errorService.openandCloseError('Failed to load schedule for editing.')
    });
  }

  private fetchAllBundleResources<T>(
    initialUrl: string,
    context: HttpContext,
    maxPages = 100
  ): Observable<T[]> {
    return this.http.get<Bundle<T>>(initialUrl, { context }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= maxPages) return EMPTY;

        const nextLink = (bundle?.link ?? []).find(
          (link) => String(link?.relation ?? '').toLowerCase() === 'next'
        )?.url;
        const nextUrl = this.normalizeFhirNextLink(nextLink);

        return nextUrl
          ? this.http.get<Bundle<T>>(nextUrl, { context })
          : EMPTY;
      }),
      map((bundle) => (bundle?.entry ?? []).map((entry) => entry.resource as T).filter(Boolean)),
      reduce((resources, pageResources) => resources.concat(pageResources), [] as T[])
    );
  }

  private normalizeFhirNextLink(nextLink: string | undefined): string | null {
    const value = String(nextLink ?? '').trim();
    if (!value) return null;

    try {
      const nextUrl = new URL(value, `${this.backendUrl.replace(/\/$/, '')}/`);
      const resourcePathMatch = nextUrl.pathname.match(/\/(Schedule|Slot)(?:\/|$)/);
      const resourcePathIndex = resourcePathMatch?.index ?? -1;
      if (resourcePathIndex < 0) return value;

      const backendBase = this.backendUrl.replace(/\/$/, '');
      return `${backendBase}${nextUrl.pathname.slice(resourcePathIndex)}${nextUrl.search}${nextUrl.hash}`;
    } catch {
      return value;
    }
  }

  private buildEditSnapshot(scheduleId: string, schedule: Schedule, slots: Slot[]): EditScheduleSnapshot {
    const slotsSnapshots: EditSlotSnapshot[] = [];
    const slotsByKey = new Map<SlotKey, EditSlotSnapshot>();
    const bookedSlotsByKey = new Map<SlotKey, EditSlotSnapshot>();

    for (const slot of slots) {
      const id = (slot?.id ?? '').toString();
      if (!id) continue;
      const start = slot.start ?? '';
      const end = slot.end ?? '';
      if (!start || !end) continue;

      const key = this.slotKey(start, end);
      const status = String((slot as any).status ?? 'free');
      const snap: EditSlotSnapshot = { id, key, resource: slot, status };
      slotsSnapshots.push(snap);
      slotsByKey.set(key, snap);
      if (this.isBookedSlotStatus(status)) bookedSlotsByKey.set(key, snap);
    }

    const planningHorizonStart = (schedule?.planningHorizon as any)?.start ?? null;
    const planningHorizonEnd = (schedule?.planningHorizon as any)?.end ?? null;

    return {
      scheduleId,
      schedule,
      planningHorizonStart,
      planningHorizonEnd,
      slots: slotsSnapshots,
      slotsByKey,
      bookedSlotsByKey,
    };
  }

  private applySnapshotToForm(snapshot: EditScheduleSnapshot): void {
    if (!this.scheduleForm) return;

    // Clear all weekday arrays.
    const allWeekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    for (const weekday of allWeekdays) {
      const arr = this.slotsPerWeekday.get(weekday);
      if (arr && arr instanceof FormArray) {
        while (arr.length > 0) arr.removeAt(0, { emitEvent: false });
        arr.enable({ emitEvent: false });
      }
    }

    // Use planningHorizon if present; otherwise infer from slots.
    const inferredStart = snapshot.planningHorizonStart ?? snapshot.slots[0]?.resource?.start ?? null;
    const inferredEnd = snapshot.planningHorizonEnd ?? snapshot.slots[0]?.resource?.end ?? null;
    if (inferredStart) this.scheduleForm.get(['date', 'start'])?.setValue(new Date(inferredStart), { emitEvent: false });
    if (inferredEnd) this.scheduleForm.get(['date', 'end', 'value'])?.setValue(new Date(inferredEnd), { emitEvent: false });

    // Group slots into weekday + HH:mm ranges (local-time based, consistent with ScheduleService generation).
    const weekdayToRanges = new Map<string, Array<{ start: string; end: string }>>();
    const weekdaysPresent = new Set<string>();

    for (const s of snapshot.slots) {
      const startIso = this.normalizeInstant(s.resource.start) ?? s.resource.start ?? '';
      const endIso = this.normalizeInstant(s.resource.end) ?? s.resource.end ?? '';
      if (!startIso || !endIso) continue;

      const weekday = this.localInstantToWeekday(startIso);
      if (!weekday) continue;
      weekdaysPresent.add(weekday);

      const startHHmm = this.localInstantToHHmm(startIso);
      const endHHmm = this.localInstantToHHmm(endIso);
      if (!startHHmm || !endHHmm) continue;

      const list = weekdayToRanges.get(weekday) ?? [];
      if (!list.some((range) => range.start === startHHmm && range.end === endHHmm)) {
        list.push({ start: startHHmm, end: endHHmm });
      }
      weekdayToRanges.set(weekday, list);
    }

    const all = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const weekdaysArray = [...weekdaysPresent];
    const isAllDays = weekdaysPresent.size === 7;
    const isMonFri = weekdaysPresent.size === 5 && all.slice(0, 5).every((d) => weekdaysPresent.has(d));
    const recurrenceType = isAllDays ? 'all_days' : (isMonFri ? 'mon_fri' : 'weekdays');

    this.scheduleForm.get(['dateRecurrence', 'type'])?.setValue(recurrenceType, { emitEvent: false });
    this.scheduleForm.get(['dateRecurrence', 'value'])?.setValue(weekdaysArray, { emitEvent: false });

    // Apply the visibility/enable-disable logic.
    this.syncWeekdaySlotsFromRecurrence();

    // Populate arrays.
    for (const [weekday, ranges] of weekdayToRanges.entries()) {
      const arr = this.slotsPerWeekday.get(weekday);
      if (!arr || !(arr instanceof FormArray)) continue;

      while (arr.length > 0) arr.removeAt(0, { emitEvent: false });
      for (const r of ranges) {
        const g = this.createSlotGroupWithValues(r.start, r.end);
        arr.push(g, { emitEvent: false });
      }
      arr.updateValueAndValidity({ emitEvent: false });
    }

    this.slotsPerWeekday.updateValueAndValidity({ emitEvent: false });
    this.recalculateTruthSource();

    // Snapshot application uses emitEvent:false, so explicitly check conflicts once after load.
    this.checkPlanningHorizonConflicts({ showBlockingError: false, hardFailOnError: false }).subscribe();
  }

  private localInstantToWeekday(instant: string): string | null {
    const d = new Date(instant);
    if (Number.isNaN(d.getTime())) return null;
    const idx = d.getDay();
    const map = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return map[idx] ?? null;
  }

  private localInstantToHHmm(instant: string): string | null {
    const d = new Date(instant);
    if (Number.isNaN(d.getTime())) return null;
    const hh = d.getHours().toString().padStart(2, '0');
    const mm = d.getMinutes().toString().padStart(2, '0');
    return `${hh}:${mm}`;
  }

  private createSlotGroup(): FormGroup {
    // This is the “SubControl” you referenced, but with a group-level validator too.
    return this.fb.group(
      {
        start: ['', Validators.required],
        end: ['', Validators.required],
      },
      {
        validators: [this.timeRangeValidator()],
        updateOn: 'change',
      }
    );
  }

  private timeRangeValidator(): ValidatorFn {
    // Angular validation function for a slot group:
    // - both start and end must be present (handled by required)
    // - start time must be strictly before end time
    //
    // We intentionally do NOT allow crossing midnight here because
    // meeting slots usually represent a single-day window.
    return (control: AbstractControl): ValidationErrors | null => {
      const startValue = control.get('start')?.value;
      const endValue = control.get('end')?.value;
      if (!startValue || !endValue) return null;

      const startMinutes = this.timeValueToMinutes(startValue);
      const endMinutes = this.timeValueToMinutes(endValue);
      if (startMinutes === null || endMinutes === null) return { invalidTime: true };

      if (endMinutes <= startMinutes) return { timeRangeOrder: true };
      return null;
    };
  }

  private noOverlappingRangesValidator(): ValidatorFn {
    // Validator for a weekday FormArray: ensures none of the time ranges overlap.
    // Overlap rule (half-open): [aStart, aEnd) overlaps [bStart, bEnd) if
    // aStart < bEnd AND bStart < aEnd
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof FormArray)) return null;

      const ranges: Array<{ start: number; end: number }> = [];
      for (const child of control.controls) {
        const startValue = child.get('start')?.value;
        const endValue = child.get('end')?.value;
        if (!startValue || !endValue) continue;

        const startMinutes = this.timeValueToMinutes(startValue);
        const endMinutes = this.timeValueToMinutes(endValue);
        if (startMinutes === null || endMinutes === null) continue;
        if (endMinutes <= startMinutes) continue;

        ranges.push({ start: startMinutes, end: endMinutes });
      }

      ranges.sort((a, b) => a.start - b.start);
      for (let i = 1; i < ranges.length; i++) {
        const prev = ranges[i - 1];
        const cur = ranges[i];
        if (cur.start < prev.end) return { overlappingTimeRanges: true };
      }

      return null;
    };
  }

  private attachWeekdayArrayValidators(): void {
    // Apply overlap validator to each weekday array.
    const weekdayKeys = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
    for (const key of weekdayKeys) {
      const control = this.slotsPerWeekday.get(key);
      if (control && control instanceof FormArray) {
        control.setValidators([this.noOverlappingRangesValidator()]);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  private setupTruthSourceRecalculation(): void {
    // Keep `slotsTruthSource` consistent with the form state.
    // Recompute only when all relevant inputs are valid.
    this.slotsPerWeekday.valueChanges
      .pipe(debounceTime(150), startWith(this.slotsPerWeekday.value))
      .subscribe(() => this.recalculateTruthSource());

    this.scheduleForm?.get(['date', 'start'])?.valueChanges
      .pipe(debounceTime(150), startWith(this.scheduleForm?.get(['date', 'start'])?.value))
      .subscribe(() => this.recalculateTruthSource());

    this.scheduleForm?.get(['date', 'end', 'value'])?.valueChanges
      .pipe(debounceTime(150), startWith(this.scheduleForm?.get(['date', 'end', 'value'])?.value))
      .subscribe(() => this.recalculateTruthSource());
  }

  private recalculateTruthSource(): void {
    // Build all slots from slotsPerWeekday + scheduleForm.date interval.
    // This is gated so we don't thrash while the user is still typing.
    const dateIntervalStart = this.scheduleForm?.get(['date', 'start'])?.value;
    const dateIntervalEnd = this.scheduleForm?.get(['date', 'end', 'value'])?.value;
    const weekdayKeys = this.selectedWeekdays;

    console.log('[CreateSchedule] recalculateTruthSource:', {
      dateIntervalStart,
      dateIntervalEnd,
      weekdayKeys,
      formInvalid: this.slotsPerWeekday.invalid,
      formStatus: this.slotsPerWeekday.status
    });

    if (!dateIntervalStart || !dateIntervalEnd) {
      this.slotsTruthSource = [];
      this.onTruthSourceCleared();
      return;
    }

    if (this.slotsPerWeekday.invalid) {
      console.warn('[CreateSchedule] slotsPerWeekday is INVALID. Details:', 
        Object.keys(this.slotsPerWeekday.controls)
          .map(k => ({ day: k, status: this.slotsPerWeekday.get(k)?.status, errors: this.slotsPerWeekday.get(k)?.errors }))
      );
      this.slotsTruthSource = [];
      this.onTruthSourceCleared();
      return;
    }

    const nextTruth: TruthSlot[] = [];

    for (const weekday of weekdayKeys) {
      const control = this.slotsPerWeekday.get(weekday);
      if (!control || !(control instanceof FormArray)) continue;

      for (const slotGroup of control.controls) {
        // Each slotGroup is a {start,end} FormGroup
        const startValue = slotGroup.get('start')?.value;
        const endValue = slotGroup.get('end')?.value;
        const timeStart = this.timeValueToHHmm(startValue);
        const timeEnd = this.timeValueToHHmm(endValue);
        if (!timeStart || !timeEnd) continue;

        // Convert this weekday+time-range into ISO datetime slots across the date interval.
        const converted = this.scheduleService.setDateTimesFromWeeklyRecurrenceAndDateInterval({
          dateIntervalStart,
          dateIntervalEnd,
          chosenWeekDay: weekday,
          timeStart,
          timeEnd,
        });

        // Validate conflicts incrementally as we build (reuse ScheduleService.checkForConflicts).
        for (const candidate of converted) {
          const conflict = this.scheduleService.checkForConflicts(nextTruth, candidate);
          if (conflict.hasConflict) {
            // Mark the offending group as conflicting so UI can react.
            slotGroup.setErrors({ ...(slotGroup.errors ?? {}), slotConflict: true });
            this.slotsTruthSource = [];
            this.onTruthSourceCleared();
            return;
          }
        }

        // If no conflicts, append.
        nextTruth.push(...converted);
      }
    }

    // Clear any previous conflict errors on groups (best-effort).
    for (const weekday of weekdayKeys) {
      const control = this.slotsPerWeekday.get(weekday);
      if (!control || !(control instanceof FormArray)) continue;
      for (const slotGroup of control.controls) {
        if (slotGroup.errors?.['slotConflict']) {
          const { slotConflict, ...rest } = slotGroup.errors;
          slotGroup.setErrors(Object.keys(rest).length ? rest : null);
        }
      }
    }

    this.slotsTruthSource = nextTruth;
    this.onTruthSourceUpdated();
    this.updateFilteredPreviewSlots();
  }

  private timeValueToHHmm(value: any): string | null {
    // Accepts either:
    // - "HH:mm" strings
    // - Date objects (Material timepicker often emits Date)
    // - date-like strings (fallback)
    if (!value) return null;
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) return null;
      return `${value.getHours().toString().padStart(2, '0')}:${value.getMinutes().toString().padStart(2, '0')}`;
    }
    if (typeof value === 'string') {
      const raw = value.trim();
      if (!raw) return null;
      const m = /^([0-9]{1,2}):([0-9]{2})$/.exec(raw);
      if (m) {
        const hh = Number(m[1]);
        const mm = Number(m[2]);
        if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
        return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
      }
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) {
        return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
      }
    }
    return null;
  }

  private timeValueToMinutes(value: any): number | null {
    const hhmm = this.timeValueToHHmm(value);
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  }

  private hhmmToDate(value: string): Date | null {
    if (!value || typeof value !== 'string') return null;
    const [hh, mm] = value.split(':').map((v) => Number(v));
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    const d = new Date();
    d.setHours(hh, mm, 0, 0);
    d.setFullYear(1970, 0, 1);
    return d;
  }

  private minutesToHHmm(totalMinutes: number): string {
    const normalized = Math.max(0, Math.floor(totalMinutes));
    const hours = Math.floor(normalized / 60) % 24;
    const minutes = normalized % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  turnStartandEndDateIntoDateArray(startDate: string, endDate: string) {

    let sD = new Date(startDate).getTime();
    const eD = new Date(endDate).getTime();
    const dateArray = [new Date(sD).toISOString()]
    const twentyFourHours = 1000 * 60 * 60 * 24;
    while (sD + twentyFourHours <= eD) {
      sD += twentyFourHours;
      dateArray.push(new Date(sD).toISOString())
    }

    this.selectedDateArray = dateArray
    console.log(this.selectedDateArray);

  }
  turnTimeIntoArray(recurrence: any, startDate: string, endDate: string) {

    let sD = +(new Date(startDate).getTime());
    const eD = new Date(endDate).getTime();
    const timeArray = [new Date(sD).getHours().toString().padStart(2, '0') + ":" + new Date(sD).getMinutes().toString().padStart(2, '0')]
    let recurrenceNumber = +recurrence
    const selectedMinutes = 1000 * 60 * recurrenceNumber;
    while (sD + selectedMinutes < eD) {
      sD += selectedMinutes;
      timeArray.push(new Date(sD).getHours().toString().padStart(2, '0') + ":" + new Date(sD).getMinutes().toString().padStart(2, '0'))
    }
    this.selectedTimeArray = timeArray
    console.log(this.selectedTimeArray)
  }
  selectedDateArray?: any
  selectedTimeArray?: any

  private setupPreviewCalendarRefresh(): void {
    if (!this.scheduleForm) return;

    const startCtrl = this.scheduleForm.get(['date', 'start']);
    const endCtrl = this.scheduleForm.get(['date', 'end', 'value']);
    if (!startCtrl || !endCtrl) return;

    combineLatest([
      startCtrl.valueChanges.pipe(startWith(startCtrl.value)),
      endCtrl.valueChanges.pipe(startWith(endCtrl.value)),
    ])
      .pipe(debounceTime(0))
      .subscribe(([startValue, endValue]) => {
        this.planningHorizonLocalMs = this.computePlanningHorizonLocalMs(startValue, endValue);
        this.refreshPreviewCalendar();

        // Auto-scroll calendar to start of planning horizon (does NOT change form values or selection)
        if (this.previewCalendar && this.planningHorizonLocalMs?.startMs) {
          this.previewCalendar.activeDate = new Date(this.planningHorizonLocalMs.startMs);
        }
      });
  }

  private refreshPreviewCalendar(): void {
    // MatCalendar only recomputes `dateClass` when it re-renders.
    // Form changes and truth-source updates are *external* to MatCalendar, so force a lightweight refresh.
    this.previewCalendar?.updateTodaysDate();
  }

  private computePlanningHorizonLocalMs(startValue: any, endValue: any): { startMs: number; endMs: number } | null {
    const start = startValue instanceof Date ? startValue : (startValue ? new Date(startValue) : null);
    const end = endValue instanceof Date ? endValue : (endValue ? new Date(endValue) : null);
    if (!start || !end) return null;
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return null;

    const s = this.startOfLocalDay(start).getTime();
    const e = this.startOfLocalDay(end).getTime();
    if (e < s) return null;
    return { startMs: s, endMs: e };
  }

  // Preview Logic
  selectedPreviewDate: Date | null = null;
  filteredPreviewSlots: TruthSlot[] = [];

previewDateClass: MatCalendarCellClassFunction<Date> = (date: Date, view: 'month' | 'year' | 'multi-year'): string => {
    if (!date || view !== 'month') { return ''; }

    const classes: string[] = [];
    const isFormMode = this.slotsTruthSource.length > 0;
    const dayKey = this.toLocalDayKey(date);

    // Range highlight (only in form mode)
    if (isFormMode) {
        const range = this.planningHorizonLocalMs;
        if (range) {
            const dMs = this.startOfLocalDay(date).getTime();
            if (dMs >= range.startMs && dMs <= range.endMs) {
                classes.push('in-range');
                if (dMs === range.startMs) { classes.push('range-start'); }
                if (dMs === range.endMs) { classes.push('range-end'); }
            }
        }
        if (this.previewSlotDayKeys.has(dayKey)) {
            classes.push('available-date');
        }
    } else {
        // Saved slots mode
        if (this.savedSlotDayKeys.has(dayKey)) {
            classes.push('available-date');
        }
    }

    return classes.join(' ');
};

  onPreviewDateSelected(date: Date | null) {
    if (!date) {
      this.selectedPreviewDate = null;
      this.filteredPreviewSlots = [];
      return;
    }

    this.selectedPreviewDate = date;
    this.updateFilteredPreviewSlots();
  }

private updateFilteredPreviewSlots(): void {
    if (!this.selectedPreviewDate) {
        this.filteredPreviewSlots = [];
        return;
    }

    const sel = this.selectedPreviewDate;
    const isFormMode = this.slotsTruthSource.length > 0;

    if (isFormMode) {
        // Show unsaved form slots
        this.filteredPreviewSlots = this.slotsTruthSource.filter((s) => {
            const d = new Date(s.dateTimeStart);
            return d.getFullYear() === sel.getFullYear() &&
                d.getMonth() === sel.getMonth() &&
                d.getDate() === sel.getDate();
        });
    } else {
        // Show saved FHIR slots
        this.filteredPreviewSlots = this.savedSlots
            .filter(slot => {
                const d = new Date(slot.start ?? '');
                return d.getFullYear() === sel.getFullYear() &&
                    d.getMonth() === sel.getMonth() &&
                    d.getDate() === sel.getDate();
            })
            .map(slot => ({
                dateTimeStart: slot.start ?? '',
                dateTimeEnd: slot.end ?? ''
            }));
    }
}

  private onTruthSourceCleared(): void {
    this.previewSlotDayKeys.clear();
    this.selectedPreviewDate = null;
    this.filteredPreviewSlots = [];
    this.refreshPreviewCalendar();
  }

  private onTruthSourceUpdated(): void {
    this.previewSlotDayKeys.clear();
    for (const slot of (this.slotsTruthSource || [])) {
      const d = new Date(slot.dateTimeStart);
      if (!Number.isFinite(d.getTime())) continue;
      this.previewSlotDayKeys.add(this.toLocalDayKey(d));
    }

    this.refreshPreviewCalendar();
  }

  private setSavedSlotsForPreview(slots: Slot[]): void {
    this.savedSlots = slots;
    this.savedSlotDayKeys.clear();

    for (const slot of slots) {
      const d = new Date(slot.start ?? '');
      if (!Number.isFinite(d.getTime())) continue;
      this.savedSlotDayKeys.add(this.toLocalDayKey(d));
    }

    this.refreshPreviewCalendar();
  }

  private toLocalDayKey(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private startOfLocalDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  // NOTE: Planning-horizon highlighting is computed via cached `planningHorizonLocalMs`.
}

/*
FORM/TEMPLATE CONTROL AUDIT (CreateScheduleComponent)

Form model defined in the component (scheduleForm):
- title
- date: { start, end: { type, value } }
- time: { start, end: { type, value } }
- dateRecurrence: { type, value }
- timeRecurrence: { type, value }

Template bindings checked (create-schedule.component.html):

✅ Controls/groups that ARE correctly bound in the template
- [formGroup]="scheduleForm" guarded by @if(scheduleForm)
- formGroupName="date"
  - formControlName="start"
  - formGroupName="end"
    - formControlName="type"
    - formControlName="value"
- formGroupName="dateRecurrence"
  - formControlName="type"
- formGroupName="time"
  - start: [formControlName]="'start'" (works; equivalent to formControlName="start")
  - end: formGroupName="end" + [formControlName]="'value'"
- formGroupName="timeRecurrence"
  - value: [formControlName]="'value'"

⚠️ Controls that exist in the component form but are NOT bound/used in the template
- title (no formControlName="title" in the template)
- time.end.type (control exists but no UI binds/sets it)
- dateRecurrence.value (intended to store selected weekdays, but never actually set)
- timeRecurrence.type (control exists but no UI binds/sets it)

⚠️ Template/UI logic that does NOT currently write into the form
- Weekday chips call setValue($event, scheduleForm!.get(['dateRecurrence','value'])!),
  but setValue() only console.log’s the event and never updates the control.
  Result: dateRecurrence.value stays '' even after selecting chips.

⚠️ Validation/typing mismatches to be aware of
- timeRecurrence.value uses Validators.minLength(2) but the UI hint says "minimum : 30 mins".
  minLength(2) validates "ab" and rejects "5"; it does NOT enforce >= 30 or numeric.
  If you want minutes: consider Validators.required + Validators.min(30) + Validators.pattern(/^[0-9]+$/).
- Datepicker/timepicker usually emit Date objects, but helper method signatures are typed as string.
  Runtime currently works because new Date(Date) is valid, but the types are misleading.

⚠️ Preview note
- selectedDateArray is set to a DateRange, but <mat-calendar> [selected] typically expects a single Date.
  If the preview doesn't highlight a range, this is likely why.

Overall: there are no missing control names causing immediate reactive-form errors,
but a few controls are currently unused and one (dateRecurrence.value) is not being populated.
*/
